<template>
  <v-app>
    <!-- Global Loading Bar -->
    <v-overlay
      :value="appStore.isLoading"
      class="align-center justify-center"
      persistent
    >
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      />
      <div class="mt-4 text-center">
        <h3>{{ appStore.loadingMessage }}</h3>
      </div>
    </v-overlay>

    <!-- Navigation -->
    <NavBar v-if="showNavigation" />

    <!-- Main Content -->
    <v-main>
      <router-view />
    </v-main>

    <!-- Footer -->
    <AppFooter v-if="showFooter" />

    <!-- Global Notifications -->
    <GlobalNotifications />
  </v-app>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useAppStore } from "@/stores/app";
import { useAuthStore } from "@/stores/auth";
import NavBar from "@/components/layout/NavBar.vue";
import AppFooter from "@/components/layout/AppFooter.vue";
import GlobalNotifications from "@/components/common/GlobalNotifications.vue";
import { useAdminStore } from "@/stores/admin";

const route = useRoute();
const appStore = useAppStore();
const authStore = useAuthStore();
const adminStore = useAdminStore();

// Routes where navigation/footer should not be shown
const hideLayoutRoutes = [
  "Login",
  "Register",
  "ForgotPassword",
  "ResetPassword",
  "EmailVerification",
  "Onboarding",
];

const showNavigation = computed(() => {
  const isExcluded =
    hideLayoutRoutes.includes(route.name as string) ||
    route.name === "AdminLogin";
  return (
    !isExcluded && (authStore.isAuthenticated || adminStore.isAuthenticated)
  );
});

const showFooter = computed(() => {
  return !hideLayoutRoutes.includes(route.name as string);
});

onMounted(async () => {
  // Initialize app
  await appStore.initialize();

  // Check for stored authentication
  if (authStore.token) {
    try {
      await authStore.fetchProfile();
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      authStore.logout();
    }
  }
});
</script>

<style lang="scss">
// Global styles
html {
  overflow-y: auto !important;
  -webkit-text-size-adjust: 100% !important;
  text-size-adjust: 100% !important;
}

/* Match Android Chrome's compact component density scaling on iOS Safari & Samsung Internet */
@media screen and (max-width: 959px) {
  html {
    font-size: 14.5px !important;
  }
}
@media screen and (max-width: 480px) {
  html {
    font-size: 13.5px !important;
  }
}

.v-application {
  font-family: "Inter", "Roboto", sans-serif !important;

  .v-btn {
    text-transform: none !important;
    font-weight: 600;
  }

  .v-card {
    border-radius: 12px !important;
  }

  .v-sheet {
    border-radius: 8px !important;
  }

  .v-text-field {
    .v-field {
      border-radius: 8px !important;
    }
  }
}

// Custom scrollbar
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;

  &:hover {
    background: #a8a8a8;
  }
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

// Loading animations
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.pulse {
  animation: pulse 2s infinite;
}

// Utility classes
.cursor-pointer {
  cursor: pointer;
}

.text-gradient {
  background: linear-gradient(135deg, #4caf50, #2196f3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.elevation-hover {
  transition: box-shadow 0.3s ease-in-out;

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  }
}

// Professional shadows
.shadow-professional {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
}

.shadow-professional-hover {
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15) !important;
  }
}

// Status colors
.status-active {
  color: #4caf50 !important;
}

.status-inactive {
  color: #f44336 !important;
}

.status-pending {
  color: #ff9800 !important;
}

.status-verified {
  color: #2196f3 !important;
}

// Professional gradients
.bg-gradient-primary {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%) !important;
}

.bg-gradient-secondary {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%) !important;
}

.bg-gradient-success {
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%) !important;
}

.bg-gradient-warning {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%) !important;
}

.bg-gradient-error {
  background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%) !important;
}
</style>
